import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudArrowUp, CheckCircle, XCircle, WarningCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-white/60">{label}</Label>
    {children}
  </div>
);

const inputCls = "h-10 bg-transparent border-white/10 text-white text-sm";

export const SftpBackupCard = () => {
  const [cfg, setCfg] = useState({ host: "", port: 22, username: "", password: "", remote_dir: ".", enabled: false, protocol: "sftp" });
  const [passwordSet, setPasswordSet] = useState(false);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState("");

  const loadLogs = () => api.get("/admin/maintenance/sftp-log").then(({ data }) => setLogs(data.logs || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/maintenance/sftp-config").then(({ data }) => {
      setCfg({ host: data.host, port: data.port, username: data.username, password: "", remote_dir: data.remote_dir, enabled: data.enabled, protocol: data.protocol || "sftp" });
      setPasswordSet(data.password_set);
    }).catch(() => {});
    loadLogs();
  }, []);

  const setProtocol = (p) => setCfg((c) => ({ ...c, protocol: p, port: p === "ftps" ? 21 : 22 }));

  const save = async () => {
    setBusy("save");
    try {
      const body = { ...cfg };
      if (!body.password) delete body.password;
      const { data } = await api.put("/admin/maintenance/sftp-config", body);
      toast.success(data.message);
      if (cfg.password) setPasswordSet(true);
      setCfg((c) => ({ ...c, password: "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/maintenance/sftp-test");
      toast.success(data.message, { duration: 7000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 9000 });
    } finally { setBusy(""); }
  };

  const runNow = async (full = false) => {
    setBusy(full ? "runfull" : "run");
    try {
      const { data } = await api.post(`/admin/maintenance/sftp-run${full ? "?full=1" : ""}`);
      toast.success(data.message, { duration: 8000 });
      setTimeout(loadLogs, 5000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div className="mt-8 p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="sftp-backup-card">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <CloudArrowUp size={40} weight="duotone" className="text-emerald-400 mb-4" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Backup esterno</h2>
          <p className="text-sm text-slate-400">Invio automatico del backup completo dell'ecosistema al tuo server esterno, 2 volte al giorno alle 12:00 e alle 00:00 ora italiana (fuso Europe/Rome). Posta viene inclusa "senza email, con stati" (account, bozze, regole, stati per-email); la notte di domenica parte il backup COMPLETO con le email (BKP_Globale_FULL). Pulizia automatica SOLO di file BKP_Globale_*.zip nella cartella indicata (mai la radice): ultimi 14 file giornalieri (7 giorni) + 4 completi. Usa una cartella dedicata, es. backup-areapaq. Le credenziali restano cifrate sul portale.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-1">
          <span className="text-xs text-white/50">{cfg.enabled ? "Attivo" : "Disattivato"}</span>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="sftp-enabled-switch" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Field label="Protocollo">
          <Select value={cfg.protocol} onValueChange={setProtocol}>
            <SelectTrigger data-testid="sftp-protocol-select" className="h-10 bg-transparent border-white/10 text-white text-sm">
              <SelectValue placeholder="Scegli il protocollo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ftps" data-testid="sftp-protocol-ftps">FTPS - porta 21</SelectItem>
              <SelectItem value="sftp" data-testid="sftp-protocol-sftp">SFTP - porta 22</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Host / IP del server">
          <Input value={cfg.host} onChange={(e) => setCfg((c) => ({ ...c, host: e.target.value }))} placeholder="backup.miodominio.it" className={inputCls} data-testid="sftp-host-input" />
        </Field>
        <Field label="Porta">
          <Input type="number" value={cfg.port} onChange={(e) => setCfg((c) => ({ ...c, port: Number(e.target.value) || 22 }))} className={inputCls} data-testid="sftp-port-input" />
        </Field>
        <Field label="Utente">
          <Input value={cfg.username} onChange={(e) => setCfg((c) => ({ ...c, username: e.target.value }))} placeholder="utente-sftp" className={inputCls} data-testid="sftp-username-input" />
        </Field>
        <Field label={passwordSet ? "Password (già salvata — riscrivi solo per cambiarla)" : "Password"}>
          <Input type="password" value={cfg.password} onChange={(e) => setCfg((c) => ({ ...c, password: e.target.value }))} placeholder={passwordSet ? "••••••••" : "password"} className={inputCls} data-testid="sftp-password-input" />
        </Field>
        <Field label="Cartella di destinazione">
          <Input value={cfg.remote_dir} onChange={(e) => setCfg((c) => ({ ...c, remote_dir: e.target.value }))} placeholder="/backups/areapaq" className={inputCls} data-testid="sftp-dir-input" />
        </Field>
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <Button onClick={save} disabled={busy === "save"} data-testid="sftp-save-button"
          className="h-10 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
          {busy === "save" ? "Salvo..." : "Salva configurazione"}
        </Button>
        <Button onClick={test} disabled={busy === "test"} variant="outline" data-testid="sftp-test-button"
          className="h-10 px-6 bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full text-sm">
          {busy === "test" ? "Verifico..." : "Prova connessione"}
        </Button>
        <Button onClick={() => runNow(false)} disabled={busy === "run"} variant="outline" data-testid="sftp-run-button"
          className="h-10 px-6 bg-transparent border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 rounded-full text-sm">
          {busy === "run" ? "Avvio..." : "Esegui backup adesso"}
        </Button>
        <Button onClick={() => runNow(true)} disabled={busy === "runfull"} variant="outline" data-testid="sftp-run-full-button"
          className="h-10 px-6 bg-transparent border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 hover:text-cyan-200 rounded-full text-sm">
          {busy === "runfull" ? "Avvio FULL..." : "Esegui FULL adesso (con email)"}
        </Button>
      </div>
      {logs.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="sftp-log-list">
          <p className="text-xs tracking-[0.15em] uppercase text-white/40">Ultimi invii</p>
          {logs.slice(0, 6).map((l) => (
            <div key={l.id} className="border-b border-white/5 pb-2">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-white/70">
                  {l.status === "ok"
                    ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0" />
                    : l.status === "warning"
                      ? <WarningCircle size={14} weight="fill" className="text-amber-400 shrink-0" />
                      : <XCircle size={14} weight="fill" className="text-red-400 shrink-0" />}
                  {l.status === "error"
                    ? (l.error || "errore")
                    : `${l.filename} (${Math.round((l.size_bytes || 0) / (1024 * 1024))} MB)`}
                  {l.mode === "full" && <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 text-[10px] font-bold">FULL</span>}
                  {l.slot && <span className="text-white/35">· {l.slot}</span>}
                </span>
                <span className="text-white/35">{(l.created_at || "").slice(0, 16).replace("T", " ")} UTC</span>
              </div>
              {l.status === "warning" && (l.issues || []).map((iss, i) => (
                <p key={i} className="text-[11px] text-amber-300/90 mt-1 pl-6">⚠ {iss}</p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
